import React from 'react';
import { ExportAssessmentPhase } from '../../interfaces/iStreamedResponse';
import {
  ASSESSMENT_PHASE_LABELS,
  ExportAssessmentData,
  isPhaseOrPartNotAssessed,
  notAssessedBadgeCopy,
  notAssessedPhases,
  notAssessedSummaryText,
} from '../../utils/exportAssessment';
import { alertTriangleIcon } from './JurisdictionDispositionBadge';

/**
 * Generic per-phase "automated assessment could not be completed" badge (policy-refusal
 * resilience plan §5.6 / Slice 6). Same amber visual recipe as the jurisdiction
 * `continued_unresolved` state (.jd-badge--unresolved / .jd-warning) so a novice reads both
 * as "needs review", but deliberately NOT jurisdiction wording — it is used for the
 * non-jurisdiction phases (profile, Schedule B, the Country and Control List Check, CCL). NRC/ITAR keep
 * their coverage chips + JurisdictionDispositionBadge for package treatment.
 *
 * Self-hides unless the phase's assessment is `not_assessed`. Provider-neutral: it never
 * shows the internal failure reason, a provider/model name, or fallback provenance (D3).
 */
interface Props {
  data?: ExportAssessmentData;
  phase: ExportAssessmentPhase;
  /** `pill` = compact header/row pill only; `full` = pill + manual-review notice. */
  variant?: 'full' | 'pill';
}

const AssessmentStatusBadge: React.FC<Props> = ({ data, phase, variant = 'full' }) => {
  // "country" = the whole Country and Control List Check (either part failing shows the badge).
  if (!isPhaseOrPartNotAssessed(data, phase)) return null;
  // The merged check names the part that wasn't assessed when the other part ran.
  const { label, notice } = notAssessedBadgeCopy(data, phase);

  const pill = (
    <span className='jd-badge jd-badge--unresolved'>
      {alertTriangleIcon}
      <span>{label}</span>
    </span>
  );

  if (variant === 'pill') return pill;

  return (
    <div className='jd-disposition'>
      {pill}
      <div className='jd-warning' role='note'>
        <span className='jd-warning-icon'>{alertTriangleIcon}</span>
        <span>{notice}</span>
      </div>
    </div>
  );
};

/**
 * Package-level summary for the final result: names every phase whose automated assessment
 * could not be completed. Same amber recipe; self-hides when every phase is assessed/unknown.
 */
export const NotAssessedSummary: React.FC<{
  data?: ExportAssessmentData;
  variant?: 'full' | 'pill';
}> = ({ data, variant = 'full' }) => {
  const phases = notAssessedPhases(data);
  if (!phases.length) return null;

  const pill = (
    <span className='jd-badge jd-badge--unresolved'>
      {alertTriangleIcon}
      <span>
        {phases.length === 1
          ? `${ASSESSMENT_PHASE_LABELS[phases[0]]}: not assessed`
          : `${phases.length} checks not assessed — manual review required`}
      </span>
    </span>
  );

  if (variant === 'pill') return pill;

  return (
    <div className='jd-disposition'>
      {pill}
      <div className='jd-warning' role='note'>
        <span className='jd-warning-icon'>{alertTriangleIcon}</span>
        <span>{notAssessedSummaryText(phases)}</span>
      </div>
    </div>
  );
};

export default AssessmentStatusBadge;
